import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { TableRegular, Search20Regular, DismissCircleRegular, ChevronRight16Regular, ChevronDown16Regular } from '@fluentui/react-icons';
import { Badge, Button, Input, Popover, PopoverSurface, PopoverTrigger, Spinner, Text, makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { dataModelsService, DatasetViewListItem } from '../../api/dataModelsService';
import { ConnectorIcon } from '../../utils/connectorIcons';

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    gap: '16px',
  },
  searchBox: {
    width: '100%',
  },
  groupList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    overflowY: 'auto',
    flex: 1,
  },
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    ...shorthands.padding('8px', '12px'),
    ...shorthands.borderRadius('6px'),
    cursor: 'pointer',
    transitionProperty: 'background-color',
    transitionDuration: '120ms',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
    },
  },
  groupChevron: {
    color: tokens.colorNeutralForeground4,
    flexShrink: 0,
  },
  groupIcons: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    flexShrink: 0,
  },
  groupTitle: {
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    color: tokens.colorNeutralForeground3,
  },
  groupItems: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  viewItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    ...shorthands.padding('10px', '12px'),
    ...shorthands.borderRadius('8px'),
    cursor: 'pointer',
    transitionProperty: 'background-color',
    transitionDuration: '120ms',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2Hover,
    },
  },
  viewTopRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  viewName: {
    flex: 1,
    fontSize: '13px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  viewQuestions: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
  },
  viewStats: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground4,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    ...shorthands.padding('48px', '24px'),
    textAlign: 'center' as const,
  },
  loadingState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.padding('48px'),
  },
});

// ─── Grouping & Sorting Logic ────────────────────────────────────────────────

interface ViewGroup {
  dataModelId: string;
  dataModelName: string;
  connections: Array<{ connectionId: string; connectorId: string; connectorName: string; connectionName: string }>;
  items: DatasetViewListItem[];
}

function groupByModel(views: DatasetViewListItem[]): ViewGroup[] {
  const map = new Map<string, ViewGroup>();

  for (const view of views) {
    let group = map.get(view.dataModelId);
    if (!group) {
      // Deduplicate connections
      const connMap = new Map<string, { connectionId: string; connectorId: string; connectorName: string; connectionName: string }>();
      for (const c of view.connections) {
        if (!connMap.has(c.connectionId)) {
          connMap.set(c.connectionId, { connectionId: c.connectionId, connectorId: c.connectorId, connectorName: c.connectorName, connectionName: c.connectionName });
        }
      }
      group = {
        dataModelId: view.dataModelId,
        dataModelName: view.dataModelName,
        connections: Array.from(connMap.values()),
        items: [],
      };
      map.set(view.dataModelId, group);
    } else {
      // Merge any new connections from this view
      for (const c of view.connections) {
        if (!group.connections.some((gc) => gc.connectionId === c.connectionId)) {
          group.connections.push({ connectionId: c.connectionId, connectorId: c.connectorId, connectorName: c.connectorName, connectionName: c.connectionName });
        }
      }
    }
    group.items.push(view);
  }

  const groups = Array.from(map.values());

  // Sort views within each group by sortOrder
  for (const g of groups) {
    g.items.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }

  // Sort groups: models with fact/multi-fact views first, then by name
  groups.sort((a, b) => {
    const aHasFact = a.items.some((v) => v.viewType === 'fact' || v.viewType === 'multi_fact');
    const bHasFact = b.items.some((v) => v.viewType === 'fact' || v.viewType === 'multi_fact');
    if (aHasFact && !bHasFact) return -1;
    if (!aHasFact && bHasFact) return 1;
    return a.dataModelName.localeCompare(b.dataModelName);
  });

  return groups;
}

// ─── Component ───────────────────────────────────────────────────────────────

interface DatasetViewSelectorProps {
  onSelect: (datasetViewId: string, viewName: string) => void;
}

const DatasetViewSelector: React.FC<DatasetViewSelectorProps> = ({ onSelect }) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [viewItems, setViewItems] = useState<DatasetViewListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = useCallback((groupId: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadViews = async () => {
      setIsLoading(true);
      try {
        const views = await dataModelsService.listViews();
        if (!cancelled) setViewItems(views);
      } catch {
        // Empty list on error
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    loadViews();
    return () => { cancelled = true; };
  }, []);

  const filteredViews = useMemo(() => {
    if (!searchQuery.trim()) return viewItems;
    const q = searchQuery.toLowerCase();
    return viewItems.filter(
      (v) =>
        (v.title || v.name).toLowerCase().includes(q) ||
        v.dataModelName.toLowerCase().includes(q) ||
        v.suggestedQuestions.some((sq) => sq.toLowerCase().includes(q)) ||
        v.connections.some((c) => c.connectionName.toLowerCase().includes(q) || c.connectorName.toLowerCase().includes(q)),
    );
  }, [viewItems, searchQuery]);

  const grouped = useMemo(() => groupByModel(filteredViews), [filteredViews]);

  const handleItemClick = useCallback(
    (item: DatasetViewListItem) => {
      onSelect(item.id, item.title || item.name);
    },
    [onSelect],
  );

  if (isLoading) {
    return (
      <div className={styles.loadingState}>
        <Spinner size="medium" label="Loading views..." />
      </div>
    );
  }

  if (viewItems.length === 0) {
    return (
      <div className={styles.emptyState}>
        <TableRegular style={{ fontSize: 32, color: tokens.colorNeutralForeground4 }} />
        <Text weight="semibold">No dataset views yet</Text>
        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
          Build a data model with semantic views first, then come back here to create an AI-powered dashboard.
        </Text>
        <button
          onClick={() => navigate('/data-models')}
          style={{
            marginTop: 8,
            padding: '6px 16px',
            borderRadius: 6,
            border: 'none',
            background: tokens.colorBrandBackground,
            color: 'white',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Go to Data Models
        </button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Input
        className={styles.searchBox}
        placeholder="Search views..."
        contentBefore={<Search20Regular />}
        contentAfter={searchQuery ? (
          <Button
            appearance="subtle"
            icon={<DismissCircleRegular />}
            size="small"
            onClick={() => setSearchQuery('')}
            aria-label="Clear search"
          />
        ) : undefined}
        value={searchQuery}
        onChange={(_, data) => setSearchQuery(data.value)}
      />

      <div className={styles.groupList}>
        {grouped.map((group) => {
          const isCollapsed = collapsedGroups.has(group.dataModelId);
          return (
            <div key={group.dataModelId}>
              {/* Collapsible group header */}
              <div
                className={styles.groupHeader}
                onClick={() => toggleGroup(group.dataModelId)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleGroup(group.dataModelId); } }}
              >
                {isCollapsed
                  ? <ChevronRight16Regular className={styles.groupChevron} />
                  : <ChevronDown16Regular className={styles.groupChevron} />
                }
                <div className={styles.groupIcons}>
                  {group.connections.map((c, i) => (
                    <ConnectorIcon key={`${c.connectorId}-${i}`} connectorId={c.connectorId} name={c.connectorName} size={14} />
                  ))}
                </div>
                <span className={styles.groupTitle}>{group.dataModelName}</span>
              </div>

              {!isCollapsed && (
                <div className={styles.groupItems}>
                  {group.items.map((item) => (
                    <ViewItemRow
                      key={item.id}
                      item={item}
                      onClick={handleItemClick}
                      styles={styles}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {grouped.length === 0 && (
          <Text size={200} style={{ color: tokens.colorNeutralForeground3, textAlign: 'center', padding: 24 }}>
            No views match your search.
          </Text>
        )}
      </div>
    </div>
  );
};

// ─── Row Sub-Component ───────────────────────────────────────────────────────

const ViewItemRow: React.FC<{
  item: DatasetViewListItem;
  onClick: (item: DatasetViewListItem) => void;
  styles: ReturnType<typeof useStyles>;
}> = ({ item, onClick, styles }) => {
  const vt = item.viewType ?? 'fact';
  const typeLabel = vt === 'multi_fact' ? 'multi-fact' : vt;
  const typeColor = vt === 'dimension' ? 'informative' : vt === 'multi_fact' ? 'important' : 'success';

  const questions = item.suggestedQuestions ?? [];
  const questionsText = questions.join(' • ');

  return (
    <div
      className={styles.viewItem}
      onClick={() => onClick(item)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(item); } }}
    >
      {/* Top row: name + type badge */}
      <div className={styles.viewTopRow}>
        <span className={styles.viewName}>{item.title || item.name}</span>
        <Badge appearance="outline" color={typeColor} size="small">{typeLabel}</Badge>
      </div>

      {/* Questions row */}
      {questions.length > 0 && (
        <Popover openOnHover mouseLeaveDelay={200} positioning="before" withArrow>
          <PopoverTrigger disableButtonEnhancement>
            <Text className={styles.viewQuestions}>{questionsText}</Text>
          </PopoverTrigger>
          <PopoverSurface style={{ padding: '10px 14px', maxWidth: 340 }}>
            {questions.map((q, i) => (
              <Text key={i} size={200} block style={{ marginBottom: i < questions.length - 1 ? 4 : 0 }}>
                • {q}
              </Text>
            ))}
          </PopoverSurface>
        </Popover>
      )}

      {/* Stats row */}
      <Text className={styles.viewStats}>
        {item.measureCount} measures • {item.dimensionCount} dimensions • {item.status}
      </Text>
    </div>
  );
};

export default DatasetViewSelector;